/**
 * Badge Component
 *
 * A flexible badge for displaying statuses, priorities, labels, and tags.
 * Combines the functionality of status badges and priority badges into one component.
 *
 * @example
 * ```jsx
 * // Using variants directly
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error">Failed</Badge>
 * <Badge variant="warning">Pending</Badge>
 *
 * // Using presets for common patterns
 * <Badge preset="status" value="open" />
 * <Badge preset="priority" value="critical" />
 * <Badge preset="dueDate" value="overdue" />
 *
 * // With dot indicator
 * <Badge variant="error" dot>Critical</Badge>
 *
 * // Different styles
 * <Badge variant="success" style="solid">Approved</Badge>
 * <Badge variant="success" style="outline">Approved</Badge>
 * <Badge variant="success" style="soft">Approved</Badge>
 *
 * // Auto-calculated due date badge
 * <DueDateBadge dueDate="2024-01-15" status="in_progress" />
 * ```
 */

import moment from 'moment';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

// ============================================================================
// VARIANT STYLES
// ============================================================================

export const VARIANT_STYLES = {
	// Soft style (default) - light background with darker text
	soft: {
		success: 'bg-emerald-100 text-emerald-700',
		warning: 'bg-amber-100 text-amber-700',
		error: 'bg-red-100 text-red-700',
		info: 'bg-blue-100 text-blue-700',
		purple: 'bg-purple-100 text-purple-700',
		pink: 'bg-pink-100 text-pink-700',
		orange: 'bg-orange-100 text-orange-700',
		yellow: 'bg-yellow-100 text-yellow-700',
		default: 'bg-gray-100 text-gray-600',
		muted: 'bg-gray-50 text-gray-400',
	},
	// Outline style - border with transparent/light background
	outline: {
		success: 'border border-emerald-500 text-emerald-600 bg-white',
		warning: 'border border-amber-500 text-amber-600 bg-white',
		error: 'border border-red-500 text-red-600 bg-white',
		info: 'border border-blue-500 text-blue-600 bg-white',
		purple: 'border border-purple-500 text-purple-600 bg-white',
		pink: 'border border-pink-500 text-pink-600 bg-white',
		orange: 'border border-orange-500 text-orange-600 bg-white',
		yellow: 'border border-yellow-500 text-yellow-600 bg-white',
		default: 'border border-gray-300 text-gray-500 bg-white',
		muted: 'border border-gray-300 text-gray-400 bg-gray-50',
	},
	// Solid style - filled background with white text
	solid: {
		success: 'bg-emerald-500 text-white border border-emerald-500',
		warning: 'bg-amber-500 text-white border border-amber-500',
		error: 'bg-red-500 text-white border border-red-500',
		info: 'bg-blue-500 text-white border border-blue-500',
		purple: 'bg-purple-500 text-white border border-purple-500',
		pink: 'bg-pink-500 text-white border border-pink-500',
		orange: 'bg-orange-500 text-white border border-orange-500',
		yellow: 'bg-yellow-500 text-white border border-yellow-500',
		default: 'bg-gray-500 text-white border border-gray-500',
		muted: 'bg-gray-400 text-white border border-gray-400',
	},
};

const DOT_COLORS = {
	success: 'bg-emerald-500',
	warning: 'bg-amber-500',
	error: 'bg-red-500',
	info: 'bg-blue-500',
	purple: 'bg-purple-500',
	pink: 'bg-pink-500',
	orange: 'bg-orange-500',
	yellow: 'bg-yellow-500',
	default: 'bg-gray-400',
	muted: 'bg-gray-300',
};

// ============================================================================
// PRESETS - Common patterns for status and priority
// ============================================================================

const PRESETS = {
	status: {
		// Ticket/task statuses
		open: { label: 'Open', variant: 'error', style: 'outline' },
		incoming: { label: 'Open', variant: 'error', style: 'outline' },
		in_progress: { label: 'In Progress', variant: 'info', style: 'outline' },
		inprogress: { label: 'In Progress', variant: 'info', style: 'outline' },
		pending: { label: 'Pending', variant: 'warning', style: 'outline' },
		action_required: {
			label: 'Action Required',
			variant: 'warning',
			style: 'outline',
		},
		scheduled: { label: 'Scheduled', variant: 'info', style: 'outline' },
		resolved: { label: 'Resolved', variant: 'default', style: 'outline' },
		done: { label: 'Resolved', variant: 'default', style: 'outline' },
		closed: { label: 'Closed', variant: 'muted', style: 'outline' },
		cancelled: { label: 'Cancelled', variant: 'muted', style: 'outline' },
		completed: { label: 'Completed', variant: 'muted', style: 'outline' },
		on_hold: { label: 'On Hold', variant: 'muted', style: 'outline' },
		// Generic statuses
		active: { label: 'Active', variant: 'success', style: 'soft' },
		inactive: { label: 'Inactive', variant: 'muted', style: 'soft' },
		draft: { label: 'Draft', variant: 'default', style: 'outline' },
		published: { label: 'Published', variant: 'success', style: 'soft' },
		archived: { label: 'Archived', variant: 'muted', style: 'soft' },
		// Approval statuses
		approved: { label: 'Approved', variant: 'success', style: 'outline' },
		rejected: { label: 'Rejected', variant: 'error', style: 'soft' },
		review: { label: 'In Review', variant: 'warning', style: 'soft' },
		deprecated: { label: 'Deprecated', variant: 'error', style: 'soft' },
		skipped: { label: 'Skipped', variant: 'default', style: 'outline' },

		super_admin: { label: 'SUPERADMIN', variant: 'info', style: 'outline' },
	},
	priority: {
		// Standard priority levels
		critical: { label: 'Critical', variant: 'error', style: 'outline' },
		urgent: { label: 'Urgent', variant: 'error', style: 'soft' },
		high: { label: 'High', variant: 'orange', style: 'soft' },
		major: { label: 'High', variant: 'orange', style: 'outline' },
		medium: { label: 'Medium', variant: 'yellow', style: 'soft' },
		minor: { label: 'Medium', variant: 'yellow', style: 'outline' },
		normal: { label: 'Normal', variant: 'yellow', style: 'soft' },
		low: { label: 'Low', variant: 'default', style: 'soft' },
		trivial: { label: 'Low', variant: 'default', style: 'soft' },
		none: { label: 'None', variant: 'muted', style: 'soft' },
		// Numeric priority levels (P0-P4)
		p0: { label: 'P0', variant: 'error', style: 'soft' },
		p1: { label: 'P1', variant: 'orange', style: 'soft' },
		p2: { label: 'P2', variant: 'yellow', style: 'soft' },
		p3: { label: 'P3', variant: 'default', style: 'soft' },
		p4: { label: 'P4', variant: 'muted', style: 'soft' },
	},
	dueDate: {
		// Date-based status indicators
		overdue: { label: 'Overdue', variant: 'error', style: 'outline' },
		due_today: { label: 'Due Today', variant: 'orange', style: 'outline' },
		due_soon: { label: 'Due Soon', variant: 'warning', style: 'outline' },
		upcoming: { label: 'Upcoming', variant: 'info', style: 'outline' },
		on_track: { label: 'On Track', variant: 'success', style: 'outline' },
		no_date: { label: 'No Date', variant: 'muted', style: 'outline' },
		normal: { label: '', variant: 'muted', style: 'outline' },
	},
	category: {
		// SOP/document categories
		equipment: { label: 'Equipment', variant: 'info', style: 'soft' },
		process: { label: 'Process', variant: 'purple', style: 'soft' },
		safety: { label: 'Safety', variant: 'orange', style: 'soft' },
	},
};

// ============================================================================
// SIZE CLASSES
// ============================================================================

const SIZE_CLASSES = {
	xs: 'px-2 py-0.5 text-[10px]',
	sm: 'px-2.5 py-0.5 text-xs',
	md: 'px-3 py-1 text-sm',
	lg: 'px-4 py-1.5 text-base',
};

const DOT_SIZE_CLASSES = {
	xs: 'w-1.5 h-1.5',
	sm: 'w-2 h-2',
	md: 'w-2 h-2',
	lg: 'w-2.5 h-2.5',
};

// ============================================================================
// COMPONENT
// ============================================================================

const Badge = ({
	// Content
	children,
	value,
	label,

	// Styling
	variant = 'default',
	style = 'soft',
	size = 'sm',
	dot = false,
	rounded = 'full', // 'full', 'lg', 'md', 'sm', 'none'

	// Presets
	preset,

	// Custom className
	className = '',
	onClick,
}) => {
	// Resolve preset if provided
	let resolvedLabel = label || children;
	let resolvedVariant = variant;
	let resolvedStyle = style;

	if (preset && value) {
		const presetConfig = PRESETS[preset];
		if (presetConfig) {
			const normalizedValue = String(value)
				.toLowerCase()
				.replace(/[\s-]/g, '_');
			const config = presetConfig[normalizedValue];
			if (config) {
				resolvedLabel = resolvedLabel || config.label;
				resolvedVariant = config.variant;
				resolvedStyle = config.style;
			} else {
				// Fallback for unknown values
				resolvedLabel = resolvedLabel || value;
			}
		}
	}

	// Get variant styles
	const styleConfig = VARIANT_STYLES[resolvedStyle] || VARIANT_STYLES.soft;
	const variantClass = styleConfig[resolvedVariant] || styleConfig.default;
	const dotColor = DOT_COLORS[resolvedVariant] || DOT_COLORS.default;

	// Rounded classes
	const roundedClasses = {
		full: 'rounded-full',
		lg: 'rounded-lg',
		md: 'rounded-md',
		sm: 'rounded-sm',
		none: 'rounded-none',
	};

	return (
		<span
			className={classNames(
				'inline-flex items-center gap-1.5 font-medium whitespace-nowrap',
				SIZE_CLASSES[size] || SIZE_CLASSES.sm,
				roundedClasses[rounded] || roundedClasses.full,
				variantClass,
				className,
			)}
			onClick={typeof onClick === 'function' ? () => onClick() : () => {}}
		>
			{dot && (
				<span
					className={classNames(
						'rounded-full flex-shrink-0',
						DOT_SIZE_CLASSES[size] || DOT_SIZE_CLASSES.sm,
						dotColor,
					)}
				/>
			)}
			{resolvedLabel}
		</span>
	);
};

// ============================================================================
// CONVENIENCE EXPORTS - Preset wrappers for common use cases
// ============================================================================

/**
 * StatusBadge - Convenience wrapper for status badges
 * @example <StatusBadge status="open" />
 */
export const StatusBadge = ({ status, ...props }) => (
	<Badge preset="status" value={status} {...props} />
);

/**
 * PriorityBadge - Convenience wrapper for priority badges
 * @example <PriorityBadge priority="high" />
 */
export const PriorityBadge = ({ priority, showDot, ...props }) => (
	<Badge preset="priority" value={priority} dot={showDot} {...props} />
);

/**
 * Calculate due date status based on date and task status
 * @param {string|Date} dueDate - The due date
 * @param {string} status - The task/work order status (completed, cancelled, etc.)
 * @param {number} dueSoonDays - Number of days to consider "due soon" (default: 3)
 * @returns {{ status: string, label: string }}
 */
export const getDueDateStatus = (dueDate, status, dueSoonDays = 3) => {
	// If no date or already completed/cancelled, return normal
	if (
		!dueDate ||
		['completed', 'cancelled', 'closed', 'resolved'].includes(status)
	) {
		return { status: 'normal', label: '' };
	}

	const now = moment().startOf('day');
	const due = moment(dueDate).startOf('day');
	const daysUntilDue = due.diff(now, 'days');

	if (daysUntilDue < 0) {
		return { status: 'overdue', label: 'Overdue' };
	}
	if (daysUntilDue === 0) {
		return { status: 'due_today', label: 'Due Today' };
	}
	if (daysUntilDue <= dueSoonDays) {
		return { status: 'due_soon', label: 'Due Soon' };
	}
	if (daysUntilDue <= 7) {
		return { status: 'upcoming', label: 'Upcoming' };
	}
	return { status: 'on_track', label: 'On Track' };
};

/**
 * DueDateBadge - Auto-calculated due date status badge
 * @example <DueDateBadge dueDate="2024-01-15" status="in_progress" />
 */
export const DueDateBadge = ({
	dueDate,
	status,
	dueSoonDays = 3,
	showLabel = true,
	hideOnTrack = true,
	size = 'md',
	...props
}) => {
	const { status: dueDateStatus, label } = getDueDateStatus(
		dueDate,
		status,
		dueSoonDays,
	);

	// Optionally hide badge when on track or normal
	if (hideOnTrack && ['on_track', 'normal'].includes(dueDateStatus)) {
		return null;
	}

	return (
		<Badge
			preset="dueDate"
			value={dueDateStatus}
			label={showLabel ? label : undefined}
			size
			{...props}
		/>
	);
};

export default Badge;
